import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  input,
  Input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
} from 'rxjs';

import {
  ERROR_HANDLING,
  ErrorHandlingComponent,
  type ExclamationErrorPosition,
} from '@blaze-shared/shared-ui';
import { isDeepEqual } from '@blaze-shared/shared-util';
import { WorkflowsApiService } from '@blaze-shared/workflow-data-access';

import { IntelliContextService } from '../../core/intelli-context.service';
import { IntelliConfig, IntelliError, IntelliValueInfo } from '../../core/models/intellisense';
import {
  FormulaContext,
  FormulaContextService,
} from '../formula-editor/formula-context-provider/formula-context.service';
import { FormulaEditorVisibilityService } from '../formula-editor/formula-editor-visibility.service';
import { FormulaEditorComponent } from '../formula-editor/formula-editor.component';

@UntilDestroy()
@Component({
  selector: 'blaze-intellisense',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './intellisense.component.html',
  providers: [{ provide: ERROR_HANDLING, useExisting: IntellisenseComponent }],
  imports: [FormulaEditorComponent, AsyncPipe],
  styles: [
    `
      :host {
        position: relative;
      }
    `,
  ],
})
export class IntellisenseComponent extends ErrorHandlingComponent implements OnChanges {
  private readonly workflowsApiService = inject(WorkflowsApiService);
  private readonly formulaVisibilityService = inject(FormulaEditorVisibilityService);

  private readonly contextService = inject(FormulaContextService, { optional: true });
  private readonly intelliContextService = inject(IntelliContextService);

  initialHeight = input<number | undefined>();

  focusLost = output<IntelliValueInfo | null | undefined>();
  value = output<IntelliValueInfo>();
  valueError = output<IntelliError>();

  @Input() config: IntelliConfig | null = null;
  @Input() colorMarkdown = false;
  @Input() disabled = false;
  @Input() @HostBinding('class.with-theme') useAppTheme = false;
  @Input() errorsPosition: ExclamationErrorPosition = 'top-right';

  private readonly internalValue$$ = new BehaviorSubject<IntelliValueInfo | null>(null);
  private readonly config$$ = new BehaviorSubject<IntelliConfig | null>(null);

  internalErrors = signal<string[]>([]);

  shouldShowFormulaEditor$ = this.config$$.pipe(
    filter((config) => !!config),
    map((config) => this.formulaVisibilityService.shouldShowFormulaEditor(config)),
    distinctUntilChanged(),
  );

  @Input() set validationErrors(value: string[]) {
    this.internalErrors.set(value);
  }

  constructor() {
    super();
    const errorMessage$ = this.internalValue$$.pipe(
      debounceTime(100),
      switchMap((value: IntelliValueInfo | undefined | null) => {
        if (!value) {
          return of('');
        }
        const workflowId = this.config?.workflow?.id;
        const nodeId = this.config?.workflowNode?.id;
        if (!value.isFormulaValue || !workflowId || !nodeId) {
          return of('');
        }
        const formulaToValidate = value.value ?? '';
        if (!formulaToValidate) {
          return of('');
        }
        return this.workflowsApiService
          .postVerifyFormula(workflowId, formulaToValidate, nodeId)
          .pipe(
            map((response) => (response.hasError ? response.error : '')),
            catchError(() => of('')),
          );
      }),
    );

    errorMessage$.pipe(untilDestroyed(this)).subscribe((errorMessage) => {
      this.internalErrors.update((errors) => [...errors, errorMessage].filter(Boolean));
      if (!this.config?.id) {
        return;
      }
      this.valueError.emit({
        id: this.config.id,
        errorMessage,
      });
    });

    if (this.contextService) {
      this.contextService.value$
        .pipe(untilDestroyed(this))
        .subscribe((context: FormulaContext | undefined | null) => {
          this.intelliContextService.updateUrlVariables(context?.urlVariables ?? []);
        });
    }

    this.shouldShowFormulaEditor$.pipe(untilDestroyed(this)).subscribe((isVisible) => {
      if (!isVisible && !!this.config?.valueInfo?.value) {
        this.clearInputValue();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.config$$.next(this.config);
    }
  }

  onFormulaChange(value: IntelliValueInfo): void {
    this.value.emit(value);
  }

  override setErrors(errors: any[]): void {
    if (isDeepEqual(this.internalErrors(), errors)) {
      return;
    }
    this.internalErrors.set(errors);
  }

  clearInputValue(): void {
    this.value.emit({
      value: '',
      displayInfos: [],
    });
  }
}
